/**
 * Support Ticket API
 */

import { callSingle } from '@/lib/rpc/facade';
import type {
  CreateTicketParams,
  ListTicketsResponse,
  TicketDetailResponse,
  TicketStatus,
} from './types';

/**
 * List user's support tickets
 */
export async function listUserTickets(
  status?: TicketStatus,
  limit = 20,
  offset = 0
): Promise<ListTicketsResponse> {
  const result = await callSingle<ListTicketsResponse>('support_list_user_tickets', {
    p_status: status || null,
    p_limit: limit,
    p_offset: offset,
  });

  if (result.error) {
    return {
      success: false,
      total: 0,
      tickets: [],
      error: result.error.message,
    };
  }

  return result.data || { success: false, total: 0, tickets: [], error: 'No data' };
}

/**
 * Create a new support ticket
 */
export async function createTicket(
  params: CreateTicketParams
): Promise<{ success: boolean; ticket_id?: string; created_at?: string; error?: string }> {
  const result = await callSingle<{ success: boolean; ticket_id?: string; created_at?: string; error?: string }>(
    'support_create_ticket',
    {
      p_subject: params.subject,
      p_message: params.message,
      p_category: params.category || 'general',
      p_priority: params.priority || 'normal',
      p_metadata: params.metadata || {},
    }
  );

  if (result.error) {
    return { success: false, error: result.error.message };
  }

  return result.data || { success: false, error: 'No data' };
}

/**
 * Get ticket detail with replies
 */
export async function getTicketDetail(ticketId: string): Promise<TicketDetailResponse> {
  const result = await callSingle<TicketDetailResponse>('support_get_ticket_detail', {
    p_ticket_id: ticketId,
  });

  if (result.error) {
    return {
      success: false,
      ticket: {} as TicketDetailResponse['ticket'],
      replies: [],
      error: result.error.message,
    };
  }

  return result.data || { success: false, ticket: {} as TicketDetailResponse['ticket'], replies: [], error: 'No data' };
}

/**
 * Add reply to ticket
 */
export async function addTicketReply(
  ticketId: string,
  content: string,
  attachments: unknown[] = []
): Promise<{ success: boolean; reply_id?: string; error?: string }> {
  const result = await callSingle<{ success: boolean; reply_id?: string; error?: string }>(
    'support_add_ticket_reply',
    {
      p_ticket_id: ticketId,
      p_content: content,
      p_attachments: attachments,
    }
  );

  if (result.error) {
    return { success: false, error: result.error.message };
  }

  return result.data || { success: false, error: 'No data' };
}

/**
 * Rate ticket satisfaction
 */
export async function rateTicket(
  ticketId: string,
  rating: number
): Promise<{ success: boolean; error?: string }> {
  const result = await callSingle<{ success: boolean; error?: string }>(
    'support_rate_ticket',
    {
      p_ticket_id: ticketId,
      p_rating: rating,
    }
  );

  if (result.error) {
    return { success: false, error: result.error.message };
  }

  return result.data || { success: false, error: 'No data' };
}
